import React, {
  useContext,
  useCallback,
  useRef,
  useState,
  useEffect,
} from 'react';
import { connect } from 'react-redux';
import { compose } from 'redux';

import { colors } from '@prisjakt/ui-base';
import Logo from '@prisjakt/ui-logo';
import { Direction } from '@prisjakt/ui';
import { AppContext } from '@prisjakt/ui-app-context';
import { withViewport } from '@prisjakt/ui-viewport';
import { TonalButton, ButtonTone } from '@prisjakt/ui/button/variants';
import Clickable from '@prisjakt/ui-clickable';
import Viewport from '../../components/viewport';
import headerActions from './actions';
import searchActions from '../search/actions/search';
import menuActions from '../navigation/actions/desktop';
import mobileMenuActions from '../navigation/actions/mobile-menu';
import NavigationMenu from '../navigation/index';
import MobileMenuButton from '../navigation/components/mobile-menu-button';
import Changelog from '../changelog/desktop-wrapper';

import { useChangelogPositionExperiment } from '../../hooks/experiments';

import { AppState } from '../../reducers';

import * as S from './styles';
import { useEventListener } from 'src/hooks/use-event-listener';
import SearchBar from 'src/components/search-bar';
import { Hideable } from '@prisjakt/ui/hideable';
import { useLocation } from 'react-router';

type OwnProps = {
  viewport: {
    top: number;
  };
};

type StateProps = {
  mobileMenuOpen?: boolean;
  megaMenuOpen?: boolean;
  showSearchBar: boolean;
  me: AppState['areas']['account']['me'];
  isAuthenticated: boolean;
  ads: AppState['ads'];
};

type DispatchProps = {
  searchFocus: () => void;
  searchBlur: () => void;
  closeMobileMenu: () => void;
  toggleMenu: (state: { open: boolean }) => void;
  setShowSearchBar: (state: boolean) => void;
};

type Props = OwnProps & StateProps & DispatchProps;

const Header = ({
  mobileMenuOpen,
  megaMenuOpen = false,
  toggleMenu,
  closeMobileMenu,
  me,
  isAuthenticated,
  ads,
}: Props) => {
  const megaMenuButtonRef = useRef<HTMLButtonElement | null>(null);
  const { t, market } = useContext(AppContext);
  const renderChangelogInHeader = useChangelogPositionExperiment();
  const [searchBarVisible, setSearchBarVisible] = useState(false);
  const location = useLocation();
  let isLandingPage = location.pathname === '/';

  useEffect(() => {
    const view = ads?.view;

    if (view && typeof view === 'string') {
      isLandingPage = view.includes('landing-page');
    }

    if (isLandingPage) {
      return;
    }

    setSearchBarVisible(true);
  }, [ads?.view]);

  const toggleMegaMenu = useCallback<
    React.MouseEventHandler<HTMLButtonElement>
  >(
    e => {
      // Prevent event from triggering an "outside click" for the menu
      e.stopPropagation();

      toggleMenu({ open: !megaMenuOpen });
    },
    [toggleMenu, megaMenuOpen]
  );

  const onCloseMegaMenu = useCallback(() => {
    if (megaMenuButtonRef.current) {
      megaMenuButtonRef.current.focus();
    }
  }, []);

  const sendFocusSearchEvent = () => {
    window.scrollTo({ top: 0 });
    setTimeout(() => {
      const event = new CustomEvent('focus-search-event', {
        detail: { focus: true },
      });
      dispatchEvent(event);
    }, 60);
  };

  const handleHeroVisibleEvent = (e: CustomEvent) => {
    const isHeroVisible = e.detail.visible || false;
    setSearchBarVisible(!isHeroVisible);
  };

  useEventListener('is-hero-visible-event', handleHeroVisibleEvent);

  const menuButtonTitle = megaMenuOpen
    ? t('symbols', 'symbol.close')
    : t('header', 'categories.title');

  return (
    <>
      <S.HeaderBar>
        <S.MegaMenuItem title={menuButtonTitle} placement={Direction.Right}>
          <S.MegaMenuButton
            type="button"
            onClick={toggleMegaMenu}
            aria-label={menuButtonTitle}
            active={megaMenuOpen}
            aria-expanded={megaMenuOpen}
            aria-haspopup
            ref={megaMenuButtonRef}
            data-test="MegaMenuToggleButton"
          >
            {megaMenuOpen ? (
              <S.CloseMegaMenuIcon size={30} />
            ) : (
              <S.MegaMenuIcon size={30} />
            )}
          </S.MegaMenuButton>
        </S.MegaMenuItem>
        <S.Wrapper>
          <S.MenuColumn data-test="MobileMenuColumn">
            <S.MenuItem>
              <MobileMenuButton />
            </S.MenuItem>
          </S.MenuColumn>

          <S.LogoColumn>
            <S.LogoLink
              to="/"
              onClick={() => mobileMenuOpen && closeMobileMenu()}
            >
              <Logo
                market={market.code}
                companyName={t('meta', 'market.logo-alt')}
                styling="v2-light"
              />
            </S.LogoLink>
          </S.LogoColumn>

          <S.SearchBarWrapper $visible={searchBarVisible}>
            <SearchBar visible={searchBarVisible} />
          </S.SearchBarWrapper>

          <S.RightColumn>
            <Viewport mobile>
              <S.MenuItem>
                <Hideable visible={searchBarVisible}>
                  <S.MagnifyerButton
                    aria-label={t('header', 'search.button')}
                    onClick={sendFocusSearchEvent}
                  >
                    <S.SearchIcon size={30} />
                  </S.MagnifyerButton>
                </Hideable>
              </S.MenuItem>
            </Viewport>

            <S.UserArea>
              {renderChangelogInHeader && <Changelog />}

              {isAuthenticated ? (
                <S.AccountMenu
                  position="bottom"
                  grow="left"
                  toggleElement={
                    <S.MenuButton
                      variant="icon"
                      color="primary"
                      icon={<S.UserAvatar {...me} size={24} />}
                    />
                  }
                  toggleElementActiveStyles={{
                    background: colors.lightTheme.staticInformative700,
                  }}
                />
              ) : (
                <TonalButton
                  container={Clickable}
                  tone={ButtonTone.DARK}
                  containerProps={{ to: '/login' }}
                >
                  {t('account', 'menu.not-logged-in-button')}
                </TonalButton>
              )}
            </S.UserArea>
          </S.RightColumn>
        </S.Wrapper>
      </S.HeaderBar>

      <NavigationMenu onCloseMegaMenu={onCloseMegaMenu} />
    </>
  );
};

const mapStateToProps = (state: AppState) => ({
  mobileMenuOpen: state.areas.navigation.mobileMenu.open,
  megaMenuOpen: state.areas.navigation.megaMenu.open,
  showSearchBar: state.areas.header.showSearchBar,
  me: state.areas.account.me,
  isAuthenticated: state.areas.account.isAuthenticated,
  ads: state.ads,
});

const dispatchActions = {
  searchFocus: searchActions.searchFocus,
  searchBlur: searchActions.searchBlur,
  closeMobileMenu: mobileMenuActions.closeMenu,
  toggleMenu: menuActions.toggleMenu,
  setShowSearchBar: headerActions.setShowSearchBar,
};

export default compose(
  withViewport,
  connect(mapStateToProps, dispatchActions)
)(Header);
